import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter } from '@angular/core';
import { PatientOverviewData } from '../patient-overview.models';

@Component({
    selector: 'app-patient-basic-info',
    templateUrl: './patient-basic-info.component.html',
    styleUrls: ['./patient-basic-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PatientBasicInfoComponent {
  @Input() patient: PatientOverviewData['patient'] | null = null;
  @Input() compact = false;
  @Input() existsInSystem = true;
  @Output() changePatient = new EventEmitter<void>();
  @Output() editPatient = new EventEmitter<void>();
  @Output() createPatient = new EventEmitter<void>();

  get patientName(): string {
    if (!this.patient) {
      return '';
    }

    let name = '';
    if (this.patient.clientname) {
      name = this.patient.clientname;
    } else {
      const parts = [this.patient.firstname ?? '', this.patient.lastname ?? ''].filter(part => !!part?.trim());
      name = parts.join(' ');
    }

    // Convert to title case (Firstname Lastname)
    return this.toTitleCase(name);
  }

  private toTitleCase(str: string): string {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  get dateOfBirth(): string {
    const raw = this.patient?.dob ?? '';
    return this.formatDob(raw);
  }

  private formatDob(raw: string): string {
    if (!raw) return '';
    // Already MM/DD/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
    // YYYY-MM-DD → MM/DD/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split('-');
      return `${m}/${d}/${y}`;
    }
    // YYYYMMDD → MM/DD/YYYY
    if (/^\d{8}$/.test(raw)) {
      return `${raw.substring(4, 6)}/${raw.substring(6, 8)}/${raw.substring(0, 4)}`;
    }
    return raw;
  }

  get gender(): string {
    return this.patient?.gender ?? '';
  }

  get patientId(): string {
    if (!this.patient) {
      return '';
    }

    const fromPatientIds = Array.isArray(this.patient.patientIDs) && this.patient.patientIDs.length
      ? this.patient.patientIDs[0]?.value ?? this.patient.patientIDs[0]
      : null;

    return (
      this.patient.patientId ??
      fromPatientIds ??
      this.patient.caseName ??
      this.patient.ownerID ??
      ''
    );
  }
}

